import type { API_RESPONSE } from '@corefront/eva-fe-sdk';
import type { SagaIterator } from 'redux-saga';
import type { AnyAction } from 'redux';
import {
  select,
  call,
  put,
  throttle,
  take,
  fork,
  SagaReturnType,
} from 'redux-saga/effects';
import { intersection } from 'ramda';
import {
  bigWinsCountriesSelector,
  bigWinsCurrencyRateSelector,
} from '../../../reducers/domain/centrifugo/bigWins';
import { getCf } from '../../../../services/centrifugo';
import {
  setBigWins,
  setBigWinsCountries,
  setBigWinsCurrency,
  setBigWinsHistoryLoaded,
  setBigWinsToQueue,
} from '../../../actions/domain/centrifugo/bigWins';
import { logger } from '../index';
import { getChunksFromString } from '../../../../utils';
import { CHANNEL_NAMESPACES, PROJECTS } from '../../../../constants/centrifugo';
import { bigWinsRefreshRateSelector } from '../../../reducers/app/configuration';
import {
  SET_BIG_WINS_DATA_TO_QUEUE,
  SET_CENTRIFUGO_DISCONNECTED,
  SET_CENTRIFUGO_ERROR,
  SET_CENTRIFUGO_INITIAL_DATA,
} from '../../../constants/centrifugo';
import { userCurrencyNameSelector } from '../../../reducers/app/params';

const ENDING_EVENTS = [SET_CENTRIFUGO_ERROR, SET_CENTRIFUGO_DISCONNECTED];
export const CENTRIFUGO_LISTENED_EVENTS = [
  SET_CENTRIFUGO_INITIAL_DATA,
  ...ENDING_EVENTS,
];

export function* bigWinsInitialDataWatcher() {
  while (true) {
    const { type, payload }: AnyAction = yield take(CENTRIFUGO_LISTENED_EVENTS);

    const bigWinsChannels = (payload?.channels || [])?.filter(
      (channel: string) => channel?.startsWith(CHANNEL_NAMESPACES.BIG_WINS)
    );
    const bigWinsInitialData = payload && payload?.data?.[PROJECTS.BIG_WINS];

    if (ENDING_EVENTS.includes(type) || !bigWinsInitialData) {
      return;
    }

    const { countries, currency } = bigWinsInitialData;
    /* countries could possibly be an array, if we're using old connect method
       or an object like this {"registration": "UA","residence": "PL"} if new.
    */
    const userCountries = !Array.isArray(countries)
      ? Object.values(countries)
      : countries;

    yield put(setBigWinsCurrency(currency));
    yield put(setBigWinsCountries(userCountries));
    yield fork(bigWinsHistoryLoader, bigWinsChannels);
    yield fork(bigWinsWatcherSaga);
  }
}

const winAmountModifier = (
  win: API_RESPONSE.BigWins[200][number],
  rate: number,
  userCurrency: string
) => {
  const {
    amount: winAmount = 0,
    originalAmount = 0,
    originalCurrency = '',
  } = win;

  const isOriginalSameAsUserCurrency =
    originalAmount && originalCurrency === userCurrency;

  const amount = isOriginalSameAsUserCurrency
    ? originalAmount
    : winAmount * rate;

  return {
    ...win,
    amount,
  };
};

/**
 * modifying win amount according to user currency rate
 * @param bigWins
 * @returns {Generator<*, *, *>}
 */
export function* bigWinsRateModifier(
  bigWins: API_RESPONSE.BigWins[200] = []
): SagaIterator<API_RESPONSE.BigWins[200]> {
  const rate = yield select(bigWinsCurrencyRateSelector);
  const userCurrency = yield select(userCurrencyNameSelector);
  return bigWins?.map((win) => winAmountModifier(win, rate, userCurrency));
}

const CENTRIFUGO_HISTORY_SETTINGS = {
  limit: 5,
};
/**
 * load BigWins histories and map it to an array of BigWins
 * @param {Array<string>} channels
 * @returns {Promise<*[]>}
 */
export const loadBigWinsHistory = async (channels: string[]) => {
  const centrifuge = getCf();
  const bigWins = [];

  const bigWinsChannels =
    channels?.filter((channelName) =>
      channelName.startsWith(CHANNEL_NAMESPACES.BIG_WINS)
    ) || [];

  try {
    const bigWinsHistories = await Promise.allSettled(
      bigWinsChannels.map((channel) =>
        centrifuge.history(channel, CENTRIFUGO_HISTORY_SETTINGS)
      )
    );
    bigWinsHistories.forEach((res) => {
      if (res.status === 'fulfilled') {
        bigWins.push(
          ...res.value.publications
            ?.map((publication) => publication?.data)
            ?.reverse()
        );
      }
    });

    return bigWins;
  } catch (e) {
    // log
    logger.error('Centrifugo big wins filter error', e);
    return bigWins;
  }
};

/**
 * filtering BigWins according to banned and user's countries
 * @param {Array} bigWins
 * @returns {Generator<*, *, *>}
 */
export function* filterBigWins(bigWins: API_RESPONSE.BigWins[200]) {
  const countries: ReturnType<typeof bigWinsCountriesSelector> = yield select(
    bigWinsCountriesSelector
  );

  return bigWins?.filter((win) => {
    const listOfBannedCountries = getChunksFromString(win?.countries || '');
    return listOfBannedCountries
      ? intersection(countries, listOfBannedCountries).length === 0
      : true;
  });
}

/**
 * loading history, filtering them and changing amount according
 * to userCurrencyRate
 * @param {Array<string>} channels
 * @returns {Generator<*, void, *>}
 */
export function* bigWinsHistoryLoader(channels: string[]) {
  const bigWins: SagaReturnType<typeof loadBigWinsHistory> = yield call(
    loadBigWinsHistory,
    channels
  );
  const filteredBigWins: SagaReturnType<typeof filterBigWins> = yield call(
    filterBigWins,
    bigWins
  );
  const result: SagaReturnType<typeof bigWinsRateModifier> = yield call(
    bigWinsRateModifier,
    filteredBigWins
  );
  yield put(setBigWins(result));
  yield put(setBigWinsHistoryLoaded());
}

export function* bigWinsWatcherSaga() {
  const bigWinsDelay: ReturnType<typeof bigWinsRefreshRateSelector> =
    yield select(bigWinsRefreshRateSelector);

  function* setBigWin({ payload: win }: AnyAction) {
    yield put(setBigWins(win));
  }

  yield throttle(bigWinsDelay, SET_BIG_WINS_DATA_TO_QUEUE, setBigWin);
}

export function* bigWinHandler(win: API_RESPONSE.BigWins[200][0]) {
  const filteredBigWins: SagaReturnType<typeof filterBigWins> = yield call(
    filterBigWins,
    [win]
  );
  const result: SagaReturnType<typeof bigWinsRateModifier> = yield call(
    bigWinsRateModifier,
    filteredBigWins
  );
  // not pushing them directly to state
  // because we need a delay between updates
  yield put(setBigWinsToQueue(result));
}
